/* ─────────────────────────────────────────────────────────────
   usePlacesOptions — TanStack Query hook for places reference data.

   Fetches countries / subdivisions from the BE `places` GraphQL query
   (PG-backed, hot path). Optionally triggers an ES fuzzy fallback via
   `searchPlaces` when `allowFreeText: true` and the user types a query
   that returns no PG hits.

   Cache key: ['places', level, parentId | parentIsoCode]
   staleTime: 24h  (place hierarchies change ~yearly)
   gcTime:    7d

   Pre-codegen note: Uses raw graphql string queries (TypedDocumentNode
   import swap happens in T02 once codegen runs against the live gateway).
   ───────────────────────────────────────────────────────────── */

import { useQuery } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { graphqlClient } from '@/lib/graphql_client';
import type { ComboboxOption } from '@/components/ui/Combobox';

/* ── Types — sourced from codegen (T02) ─────────────────────────
   PlaceLevel is re-exported from @/gql/graphql so existing consumers
   (PlacesSelect, form-engine config) continue importing it from
   usePlacesOptions without a churn-inducing rename.

   PlaceOption / PlaceSearchHit are local projection shapes used by
   the hook's `options` / `searchSuggestions` outputs; the underlying
   GraphQL responses go through the codegen-typed query strings below.
   ─────────────────────────────────────────────────────────── */

import type { PlaceLevel as GqlPlaceLevel } from '@/gql/graphql';
import { PlaceByIsoCodeDocument } from '@/gql/graphql';

/** Level of a place in the hierarchy. Re-export of the codegen union. */
export type PlaceLevel = GqlPlaceLevel;

/** Local projection of the `Place` GraphQL type used by the hook output. */
export interface PlaceOption {
  id: string;
  isoCode: string | null;
  canonicalName: string;
  level?: PlaceLevel;
  parentId?: string | null;
  isActive?: boolean;
}

/** Local projection of the `PlaceSearchHit` GraphQL type. */
export interface PlaceSearchHit {
  place: PlaceOption;
  score: number;
  matchedOn: string | null;
}

/* ── Hook config / result ─────────────────────────────────── */

export interface UsePlacesOptionsConfig {
  level: 'country' | 'subdivision' | 'locality';
  /** Resolved parentId value from form state (via parentField). null/undefined = disabled. */
  parentId?: string | null;
  /** Static parent lock by ISO code — alternative to parentId. */
  parentIsoCode?: string;
  /** Enable free-text + ES fallback suggestion mode. */
  allowFreeText?: boolean;
  /**
   * Which Place field to project as the Combobox `value`.
   * Defaults to 'id' for back-compat with ENT-47 entity-level fields.
   *
   * - 'id'           → place.id (UUID) — entity country/jurisdiction
   * - 'isoCode'      → place.isoCode   — address country, person country
   * - 'canonicalName' → place.canonicalName — address region
   */
  valueField?: 'id' | 'isoCode' | 'canonicalName';
  /**
   * ENT-50: When set, the parent field uses valueField='isoCode', meaning the
   * parent's current form value is an ISO code string (e.g. "GB") rather than
   * a UUID. The hook resolves the ISO code to a UUID via `usePlaceByIsoCode`
   * before issuing the SubdivisionsByParent query.
   *
   * Set this when: level='subdivision' AND the parent country field uses valueField='isoCode'.
   */
  parentIsoCodeValue?: string | null;
}

export interface UsePlacesOptionsResult {
  /** Mapped options ready for Combobox: { value: place.id, label: place.canonicalName } */
  options: ComboboxOption[];
  /** Raw place data (needed for deprecated annotation check in PlacesSelect). */
  rawPlaces: PlaceOption[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  retry: () => void;
  /** ES fuzzy search suggestions (only populated when allowFreeText=true and query set). */
  searchSuggestions: PlaceSearchHit[];
  isSearching: boolean;
  /** Set to trigger debounced ES fallback query. */
  setSearchQuery: (q: string) => void;
}

/* ── Cache key helpers ─────────────────────────────────────── */

function buildQueryKey(
  level: string,
  parentId?: string | null,
  parentIsoCode?: string,
): readonly unknown[] {
  return ['places', level, parentId ?? parentIsoCode ?? null] as const;
}

/* ── staleTime / gcTime constants ─────────────────────────── */

const STALE_24H = 24 * 60 * 60 * 1000;
const GC_7D = 7 * 24 * 60 * 60 * 1000;
const STALE_1MIN = 60 * 1000;
const DEBOUNCE_MS = 300;

/* ── Raw GraphQL query strings (replaced by TypedDocumentNodes in T02) ── */

const COUNTRIES_QUERY = /* GraphQL */ `
  query Countries {
    places(level: COUNTRY) {
      id
      isoCode
      canonicalName
    }
  }
`;

const SUBDIVISIONS_QUERY = /* GraphQL */ `
  query SubdivisionsByParent($parentId: ID!) {
    places(parentId: $parentId, level: SUBDIVISION) {
      id
      isoCode
      canonicalName
    }
  }
`;

const SEARCH_PLACES_QUERY = /* GraphQL */ `
  query SearchPlaces($query: String!, $level: PlaceLevel, $parentId: ID, $limit: Int) {
    searchPlaces(query: $query, level: $level, parentId: $parentId, limit: $limit) {
      place {
        id
        isoCode
        canonicalName
        level
      }
      score
      matchedOn
    }
  }
`;

/* ── ENT-50: PlaceByIsoCode — uses generated TypedDocumentNode ─
   Generated by codegen against the live gateway with ENT-50 BE schema.
   PlaceByIsoCodeDocument is the typed version of the operation in
   src/graphql/operations/places.graphql.
   ─────────────────────────────────────────────────────────────── */
// PlaceByIsoCodeDocument imported from '@/gql/graphql' at top of file.

/* ── GraphQL response shapes ────────────────────────────────── */

interface PlacesResponse {
  places: PlaceOption[];
}

interface SearchPlacesResponse {
  searchPlaces: PlaceSearchHit[];
}

/* ── ENT-50: usePlaceByIsoCode ─────────────────────────────────
   Resolves a Place by ISO code for the address cascade path.
   Used when address `country` (valueField='isoCode') is the parent
   of address `region` — the isoCode string must be resolved to a
   UUID before SubdivisionsByParent can be issued.

   Uppercases input — BE resolver is case-sensitive (handover §5).
   Cache key: ['place-by-iso', normalisedCode]
   staleTime: 24h (ISO codes are stable)

   Uses PlaceByIsoCodeDocument (TypedDocumentNode) generated in T02.
   ─────────────────────────────────────────────────────────────── */

export function usePlaceByIsoCode(isoCode: string | null | undefined): {
  place: PlaceOption | null;
  isLoading: boolean;
} {
  const normalised = isoCode ? isoCode.toUpperCase() : null;

  const { data, isLoading } = useQuery({
    queryKey: ['place-by-iso', normalised],
    queryFn: async (): Promise<PlaceOption | null> => {
      // PlaceByIsoCodeDocument is the TypedDocumentNode generated from places.graphql
      const result = await graphqlClient.request(PlaceByIsoCodeDocument, { isoCode: normalised! });
      return result.place as PlaceOption | null;
    },
    enabled: Boolean(normalised),
    staleTime: STALE_24H,
    gcTime: GC_7D,
  });

  return { place: data ?? null, isLoading };
}

/* ── Hook ────────────────────────────────────────────────────── */

/**
 * TanStack Query hook for places reference data.
 *
 * @example
 * // Country dropdown (no parent)
 * const { options, isLoading } = usePlacesOptions({ level: 'country' });
 *
 * // Subdivision dropdown cascading from country field value
 * const { options, isLoading } = usePlacesOptions({
 *   level: 'subdivision',
 *   parentId: countryPlaceId,
 *   allowFreeText: true,
 * });
 */
/* ── Option projection helper ──────────────────────────────────
   Projects raw PlaceOption[] to ComboboxOption[] based on valueField.
   Default is 'id' (back-compat with ENT-47 entity-level fields).
   ─────────────────────────────────────────────────────────────── */

function projectOptions(
  places: PlaceOption[],
  valueField?: UsePlacesOptionsConfig['valueField'],
): ComboboxOption[] {
  return places.map((p) => {
    // isoCode exposed as description so users can search by code (e.g. "GB", "US")
    const description = p.isoCode ?? undefined;
    switch (valueField) {
      case 'isoCode':
        // ISO 3166-1 alpha-2 / ISO 3166-2 — stored as string e.g. "GB"
        // Dev-mode assertion: warn on unexpected format
        if (import.meta.env.DEV && p.isoCode && !/^[A-Z]{2,3}(-[A-Z0-9]{1,3})?$/i.test(p.isoCode)) {
          console.warn(`[usePlacesOptions] Unexpected isoCode format: "${p.isoCode}" for place "${p.canonicalName}"`);
        }
        return { value: p.isoCode ?? '', label: p.canonicalName, description };
      case 'canonicalName':
        // Canonical display name string — stored as-is e.g. "Bavaria"
        return { value: p.canonicalName, label: p.canonicalName, description };
      case 'id':
      default:
        // UUID (default) — entity country_of_incorporation, jurisdiction
        return { value: p.id, label: p.canonicalName, description };
    }
  });
}

export function usePlacesOptions({
  level,
  parentId,
  parentIsoCode,
  allowFreeText = false,
  valueField,
  parentIsoCodeValue,
}: UsePlacesOptionsConfig): UsePlacesOptionsResult {
  /* ── Search query state for ES fallback ── */
  const [searchQueryRaw, setSearchQueryRaw] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  // Use a ref for the timer — storing it in state would trigger a re-render on every
  // setSearchQuery call, causing Combobox's useEffect([open, onSearch]) to re-fire
  // because setSearchQuery (useCallback dep on timer) would get a new reference each
  // render, producing an infinite loop when the dropdown closes.
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setSearchQuery = useCallback((q: string) => {
    setSearchQueryRaw(q);
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    const timer = setTimeout(() => setDebouncedQuery(q), DEBOUNCE_MS);
    debounceTimerRef.current = timer;
  }, []);

  /* ── ENT-50: isoCode → UUID resolution for address cascade ──
     When the parent field uses valueField='isoCode', the parent's current
     form value is an ISO code string (not a UUID). We resolve it to a UUID
     via usePlaceByIsoCode before issuing SubdivisionsByParent.
     ─────────────────────────────────────────────────────────── */
  const { place: resolvedParentPlace, isLoading: isResolvingParent } =
    usePlaceByIsoCode(parentIsoCodeValue ?? null);

  /* ── Primary query (PG hot path) ── */
  const isCountry = level === 'country';
  const isSubdivision = level === 'subdivision';

  // Country query is always enabled for country-level fields
  const countriesEnabled = isCountry;

  // Determine effective parentId:
  // - When parentIsoCodeValue is set: use the resolved UUID (wait for resolution)
  // - When parentId is set: use it directly (entity-level UUID cascade)
  // - Otherwise: null (field disabled)
  const effectiveParentId: string | null = parentIsoCodeValue
    ? (resolvedParentPlace?.id ?? null)
    : (parentId ?? null);

  // Subdivision query requires an effective parentId and must not fire while
  // the parent ISO code is being resolved (prevents stale/premature query).
  const subdivisionsEnabled =
    isSubdivision &&
    Boolean(effectiveParentId) &&
    !(parentIsoCodeValue && isResolvingParent);

  const primaryQuery = useQuery({
    queryKey: buildQueryKey(level, effectiveParentId, parentIsoCode),
    queryFn: async (): Promise<PlaceOption[]> => {
      if (isCountry) {
        const result = await graphqlClient.request<PlacesResponse>(COUNTRIES_QUERY);
        return result.places ?? [];
      }
      if (isSubdivision && effectiveParentId) {
        const result = await graphqlClient.request<PlacesResponse>(
          SUBDIVISIONS_QUERY,
          { parentId: effectiveParentId },
        );
        return result.places ?? [];
      }
      return [];
    },
    enabled: countriesEnabled || subdivisionsEnabled,
    staleTime: STALE_24H,
    gcTime: GC_7D,
  });

  /* ── ES fallback query (only when allowFreeText=true and query ≥ 2 chars) ── */
  const searchEnabled =
    allowFreeText &&
    debouncedQuery.length >= 2 &&
    searchQueryRaw.length >= 2;

  const levelUpper = level.toUpperCase() as PlaceLevel;

  const searchQuery = useQuery({
    queryKey: ['places-search', level, effectiveParentId, debouncedQuery],
    queryFn: async (): Promise<PlaceSearchHit[]> => {
      const result = await graphqlClient.request<SearchPlacesResponse>(
        SEARCH_PLACES_QUERY,
        {
          query: debouncedQuery,
          level: levelUpper,
          parentId: effectiveParentId ?? undefined,
          limit: 10,
        },
      );
      return result.searchPlaces ?? [];
    },
    enabled: searchEnabled,
    staleTime: STALE_1MIN,
    gcTime: STALE_1MIN * 5,
  });

  /* ── Derived values ── */

  const rawPlaces = primaryQuery.data ?? [];

  // Project options based on valueField (default: 'id' for back-compat)
  const options: ComboboxOption[] = projectOptions(rawPlaces, valueField);

  const retry = useCallback(() => {
    void primaryQuery.refetch();
  }, [primaryQuery]);

  return {
    options,
    rawPlaces,
    isLoading: primaryQuery.isLoading || (!!parentIsoCodeValue && isResolvingParent),
    isError: primaryQuery.isError,
    error: primaryQuery.error as Error | null,
    retry,
    searchSuggestions: searchQuery.data ?? [],
    isSearching: searchQuery.isFetching,
    setSearchQuery,
  };
}

/* ── Single-place fetch ────────────────────────────────────────
   Fetches a single Place by ID for hydrating FK references.
   Returns deprecated rows so historical entities display correctly.
   ─────────────────────────────────────────────────────────────── */

const PLACE_QUERY = /* GraphQL */ `
  query Place($id: ID!) {
    place(id: $id) {
      id
      isoCode
      canonicalName
      level
      parentId
      isActive
    }
  }
`;

interface PlaceByIdResponse {
  place: PlaceOption | null;
}

export function usePlaceById(id: string | null | undefined): {
  place: PlaceOption | null;
  isLoading: boolean;
} {
  const { data, isLoading } = useQuery({
    queryKey: ['place', id],
    queryFn: async (): Promise<PlaceOption | null> => {
      const result = await graphqlClient.request<PlaceByIdResponse>(PLACE_QUERY, { id });
      return result.place;
    },
    enabled: Boolean(id),
    staleTime: STALE_24H,
    gcTime: GC_7D,
  });

  return { place: data ?? null, isLoading };
}
